import React from "react";
import { useNavigate } from "react-router-dom";
import { TempleWithProgress } from "@/types/temple.types";
import { TempleSvg } from "./TempleSvg";
import { Lock, CheckCircle2, Play, ArrowRight } from "lucide-react";

interface TempleCardProps {
  readonly temple: TempleWithProgress;
  readonly onAction: (temple: TempleWithProgress) => void;
}

export function TempleCard({ temple, onAction }: TempleCardProps) {
  const navigate = useNavigate();

  function handleClick() {
    if (temple.isLocked) return;
    onAction(temple);
    navigate(`/templo/${temple.id}`);
  }

  let StatusIcon = Play;
  if (temple.isCompleted) StatusIcon = CheckCircle2;
  else if (temple.isLocked) StatusIcon = Lock;

  const progress = temple.progress?.progress_percentage || 0;

  return (
    <TempleSvg
      color={temple.color}
      className={`mb-8 ${temple.isLocked ? "opacity-50" : ""}`}
      svgClassName={
        temple.isLocked
          ? "cursor-not-allowed"
          : "cursor-pointer hover:scale-[1.02] hover:drop-shadow-lg"
      }
      icon={
        <div
          className={`w-11 h-11 rounded-full bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)] border border-black/[0.06] flex items-center justify-center ${
            temple.isCompleted
              ? "text-emerald-500"
              : temple.isLocked
                ? "text-neutral-300"
                : "text-accent-800"
          }`}
        >
          <StatusIcon className="w-5 h-5" />
        </div>
      }
    >
      <div className="px-4 flex flex-col h-[320px]">
        {/* Header — fixed height */}
        <div className="text-center flex-shrink-0">
          <p className="text-[9px] text-neutral-400 font-bold uppercase tracking-[0.1em]">
            Templo {temple.order_number}
          </p>
          <h3 className="text-[15px] font-bold text-neutral-800 mt-0.5 leading-tight line-clamp-1">
            {temple.name}
          </h3>
          <p className="text-xs text-neutral-400 leading-[1.6] mt-1.5 line-clamp-6">
            {temple.subtitle
              ? `${temple.subtitle}. ${temple.description}`
              : temple.description}
          </p>
        </div>

        {/* Middle — fixed height for progress + stats or locked message */}
        <div className="flex flex-col justify-end flex-1">
          {!temple.isLocked ? (
            <>
              <div className="mb-2">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-[9px] text-neutral-400 font-semibold uppercase tracking-wider">
                    Progresso
                  </span>
                  <span className="text-[10px] font-bold text-accent-800 tabular-nums">
                    {progress}%
                  </span>
                </div>
                <div className="h-[4px] bg-black/[0.04] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${temple.isCompleted ? "bg-emerald-500" : "bg-gradient-to-r from-accent-800 to-accent-600"}`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-1 mb-2">
                <div className="text-center py-1.5 rounded-lg bg-black/[0.02]">
                  <p className="text-xs font-bold text-neutral-800 tabular-nums">
                    {temple.progress?.completed_tasks || 0}/
                    {temple.total_tasks || 0}
                  </p>
                  <p className="text-[8px] text-neutral-400 font-semibold uppercase tracking-wider">
                    Tarefas
                  </p>
                </div>
                <div className="text-center py-1.5 rounded-lg bg-black/[0.02]">
                  <p className="text-xs font-bold text-neutral-800 tabular-nums">
                    ~{temple.estimated_hours}h
                  </p>
                  <p className="text-[8px] text-neutral-400 font-semibold uppercase tracking-wider">
                    Duração
                  </p>
                </div>
                <div className="text-center py-1.5 rounded-lg bg-black/[0.02]">
                  <p className="text-xs font-bold text-accent-800 tabular-nums">
                    {temple.total_xp}
                  </p>
                  <p className="text-[8px] text-neutral-400 font-semibold uppercase tracking-wider">
                    XP
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center flex-1 mb-2">
              <Lock className="w-5 h-5 text-neutral-300 mb-1.5" />
              <p className="text-[11px] text-neutral-400 text-center">
                Complete o templo anterior para desbloquear
              </p>
            </div>
          )}
        </div>

        {/* Action Button — always at bottom */}
        <button
          className={`w-full py-2.5 rounded-xl font-semibold text-[13px] transition-all duration-200 flex items-center justify-center gap-1.5 ${
            temple.isLocked
              ? "bg-neutral-100 text-neutral-400 cursor-not-allowed"
              : temple.isCompleted
                ? "bg-emerald-500 hover:bg-emerald-600 text-white shadow-[0_1px_3px_rgba(16,185,129,0.2)]"
                : "bg-gradient-to-r from-accent-800 to-accent-600 text-white shadow-[0_1px_3px_rgba(195,150,74,0.25)] hover:shadow-[0_2px_8px_rgba(195,150,74,0.3)]"
          }`}
          disabled={temple.isLocked}
          onClick={handleClick}
          tabIndex={temple.isLocked ? -1 : 0}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && !temple.isLocked) {
              e.preventDefault();
              handleClick();
            }
          }}
          aria-label={`${temple.name} - ${temple.isLocked ? "Bloqueado" : temple.buttonText}`}
        >
          {temple.buttonText}
          {!temple.isLocked && <ArrowRight className="w-3.5 h-3.5" />}
        </button>
      </div>
    </TempleSvg>
  );
}
